import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import { useTranslate } from '../../../components/TranslationText';
import EahsForm from '../../../components/EahsForm';
import {
    FormTextInput,
    FormDateInput,
    FormFileUpload,
    FormSelect,
    FormYesNo,
} from '../../../components/FormInputs';
import { eformInput } from '../../../common/cssclasses';
import { promiseOptions } from '../Api/semenMarketApi';

/**
 * Mare Details section of the SemenCheckout form.
 * Reads form state from FormProvider context.
 *
 * @param {Array} colors - Color options for select
 * @param {Array} countries - Country options for select (origin)
 * @param {boolean} disabled - Disable all inputs (view-only)
 * @param {object|null} selectedMare - Currently selected registered mare
 * @param {function} onSelectedMareChange - Callback when mare selection changes
 */
export default function SemenMareDetails({
    colors,
    countries,
    disabled = false,
    selectedMare,
    onSelectedMareChange,
}) {
    const { translate } = useTranslate();
    const { watch, setValue, formState: { errors } } = useFormContext();
    const isRegisteredMare = watch('isRegisteredMare');
    const mareIdError = errors?.mareId;

    const [searchMare, setSearchMare] = useState('');
    const [timeoutId, setTimeoutId] = useState(0);

    const handleMareChange = (option) => {
        onSelectedMareChange(option);
        setValue('mareId', option?.value || '', { shouldValidate: true });
    };

    return (
        <EahsForm key="mareDetails" title={translate('web.mareDetails', 'Mare Details')}>
            <div className={eformInput}>
                <FormYesNo
                    name="isRegisteredMare"
                    label={translate('web.isMareRegistered', 'Is Mare Registered')}
                    required
                    disabled={disabled}
                />
            </div>

            {/* Registered mare — async search select */}
            {isRegisteredMare && (
                <div className={eformInput}>
                    <label>
                        {translate('web.mare', 'Mare')}
                        <i style={{ color: 'red', margin: 'auto 2px' }}>*</i>
                    </label>
                    <AsyncSelect
                        isClearable
                        loadOptions={(key) =>
                            promiseOptions(key, 'Mare', timeoutId, setTimeoutId)
                        }
                        defaultOptions={false}
                        onInputChange={(v) => setSearchMare(v)}
                        inputValue={searchMare}
                        value={selectedMare}
                        loadingMessage={() => translate('web.loading', 'Loading')}
                        onChange={handleMareChange}
                        placeholder={translate('web.typeToSearch', 'Type To Search') + '...'}
                        noOptionsMessage={() => translate('web.noOptions', 'No Options')}
                        isDisabled={disabled}
                        styles={{
                            control: (base) => ({
                                ...base,
                                ...(mareIdError ? { borderColor: 'red' } : {}),
                            }),
                        }}
                    />
                    {mareIdError && (
                        <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                            {mareIdError.message}
                        </div>
                    )}
                </div>
            )}

            {/* Unregistered mare — manual fields */}
            {!isRegisteredMare && (
                <>
                    <div className={eformInput}>
                        <FormTextInput
                            name="mareNameEn"
                            label={translate('web.mare', 'Mare') + ' (EN)'}
                            required
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormTextInput
                            name="mareNameAr"
                            label={translate('web.mare', 'Mare') + ' (AR)'}
                            required
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormTextInput
                            name="sireNameEn"
                            label={translate('web.sire', 'Sire') + ' (EN)'}
                            required
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormTextInput
                            name="sireNameAr"
                            label={translate('web.sire', 'Sire') + ' (AR)'}
                            required
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormTextInput
                            name="damNameEn"
                            label={translate('web.dam', 'Dam') + ' (EN)'}
                            required
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormTextInput
                            name="damNameAr"
                            label={translate('web.dam', 'Dam') + ' (AR)'}
                            required
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormDateInput
                            name="mareBirthDate"
                            label={translate('web.birthDate', 'BirthDate')}
                            required
                            disabled={disabled}
                            minDate="1900-01-01"
                            maxDate={new Date().toISOString().split('T')[0]}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormSelect
                            name="colorId"
                            label={translate('web.color', 'Color')}
                            options={colors}
                            required
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormSelect
                            name="originId"
                            label={translate('web.origin', 'Origin')}
                            options={countries}
                            required
                            disabled={disabled}
                        />
                    </div>
                    <div className={eformInput}>
                        <FormFileUpload
                            name="marePassport"
                            label={translate('web.horsePassport', 'Horse Passport')}
                            required
                            disabled={disabled}
                        />
                    </div>
                </>
            )}
        </EahsForm>
    );
}
